import { Box, Link, Stack, Typography } from '@mui/material';
import { invalidatePreviousSubmissions, sendSubmissionAccessLinkEmail } from 'api/submission';
import { EMAIL_REGX, META } from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { SecondaryButton } from 'components/lib/SecondaryButton';
import { displayToastMessage } from 'components/ToastMessage';
import { useQueryParams } from 'hooks/useSearchQueryParams';
import DefaultLayout from 'layouts/defaultLayout';
import { isEmpty } from 'lodash-es';
import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';

import NewQuoteWarningModal from 'components/NewQuoteWarningModal';
import { WelcomeBackWrapperStackStyles } from './WelcomeBack.styles';

const validationSchema = yup.object({
  email: yup.string().matches(EMAIL_REGX, 'Invalid email format').required('Email is required'),
});

/**
 * The reason of a new route is to avoid the user to go back to the eligibility page
 * and not to show temporary quote number. It could confuse the user.
 */
const WelcomeBack: FC = () => {
  const navigate = useNavigate();

  const { email } = useQueryParams<{ email: string }>();

  const [openModal, setOpenModal] = useState(false);

  const { setFullscreenLoading } = useFullscreenLoader();

  const handleOnClickPrimaryButton = async () => {
    try {
      setFullscreenLoading(true, 'Sending access link');

      const response = await sendSubmissionAccessLinkEmail(email);

      displayToastMessage('SUCCESS', response.message);
    } catch (e) {
      displayBackendErrorMessage(e);
    } finally {
      setFullscreenLoading(false);
    }
  };
  const handleOnClickSecondaryButton = async () => {
    try {
      setFullscreenLoading(true, 'Invalidating previous submissions');

      const response = await invalidatePreviousSubmissions(email);

      displayToastMessage('SUCCESS', response.message);

      navigate('/', { replace: true });
    } catch (e) {
      displayBackendErrorMessage(e);
    } finally {
      setFullscreenLoading(false);
    }
  };

  const footerOptions = {
    primaryButton: {
      text: 'Continue to create new quote',
      onClickFn: handleOnClickSecondaryButton,
    },
    tertiaryButton: {
      text: 'Re-send Link',
      onClickFn: handleOnClickPrimaryButton,
    },
  };

  useEffect(() => {
    try {
      validationSchema.validateSync({ email });
    } catch (e: unknown) {
      if (e instanceof yup.ValidationError) {
        displayBackendErrorMessage({ name: 'Error', message: e.message });
      } else {
        displayBackendErrorMessage(e);
      }

      navigate('/retrieve-quote', { replace: true });
    }
  }, [email]);

  if (isEmpty(email)) {
    return null;
  }

  return (
    <DefaultLayout displayQuoteNumber={false} renderStepper={false}>
      <NewQuoteWarningModal
        footerOptions={footerOptions}
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
      <Stack sx={WelcomeBackWrapperStackStyles}>
        <Typography variant="h1">
          Please check your email for a secure link to continue your previous quote.
        </Typography>

        <Stack gap={1.4}>
          <Box>
            <Typography variant="body1" display="inline">
              Call us now at our priority line
            </Typography>

            <Link
              variant="h3"
              sx={{ ml: 0.5, textTransform: 'none', textDecoration: 'none' }}
              href={`tel:${META.PRIORITY_CONTACT_NUMBER}`}
              target="_blank"
            >
              {META.PRIORITY_CONTACT_NUMBER}
            </Link>
          </Box>

          <Box>
            <Typography variant="body1" display="inline">
              We’re available
            </Typography>

            <Typography variant="h3" display="inline" ml={0.5}>
              Monday-Friday 8am-9pm ET.
            </Typography>
          </Box>
        </Stack>

        <Stack gap={2}>
          <PrimaryButton text="Re-send link" onClick={handleOnClickPrimaryButton} />

          <SecondaryButton text="Start new quote" onClick={() => setOpenModal(true)} />
        </Stack>
      </Stack>
    </DefaultLayout>
  );
};

export default WelcomeBack;
